/**
 * AuthGuard
 * Wraps protected routes. Initiates MSAL redirect login when the user is not
 * authenticated, shows a loading state while MSAL initialises, and renders
 * children only once a valid account is confirmed.
 *
 * Post-login redirect:
 *   Before triggering loginRedirect the intended path is saved to sessionStorage.
 *   Once loggedIn becomes true (after handleRedirectPromise resolves) the user is
 *   navigated back to that path via react-router so the URL stays client-side and
 *   no extra full-page reload occurs.
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsalAuth } from './useMsalAuth';
import { environment } from '../environments/environment';
import { RETURN_PATH_KEY } from './msalConfig';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const loginTriggered = useRef(false);

  const {
    loggedIn,
    isInitialized,
    showAuthInput,
    isAuthLoading,
    processAuthenticationMode,
    authenticateWithUserId,
  } = useMsalAuth();

  // ── Save current path and trigger login once MSAL is initialised ──
  useEffect(() => {
    if (!isInitialized || loggedIn) return;
    if (loginTriggered.current) return;

    // *** KEY GUARD ***
    // If auth_return_path is already in sessionStorage it means we saved a path
    // before a loginRedirect and are now back on the redirectUri page (or the
    // path we were navigated to by MSAL). handleRedirectPromise() hasn't set
    // loggedIn=true yet, OR the account isn't in cache yet. Either way, do NOT
    // trigger another redirect — wait for loggedIn to become true naturally.
    if (sessionStorage.getItem(RETURN_PATH_KEY)) return;

    loginTriggered.current = true;

    // Persist the URL the user was trying to reach so we can restore it after
    // the redirect comes back to the configured redirectUri (e.g. /). Only save
    // non-trivial paths to avoid overwriting a real intent with '/'.
    const intendedPath = location.pathname + location.search;
    if (intendedPath && intendedPath !== '/') {
      sessionStorage.setItem(RETURN_PATH_KEY, intendedPath);
    }

    processAuthenticationMode(environment.interactionType ?? 'redirect');
  }, [isInitialized, loggedIn, location, processAuthenticationMode]);

  // ── After login succeeds, navigate back to the saved path ──
  useEffect(() => {
    if (!loggedIn) return;
    const returnPath = sessionStorage.getItem(RETURN_PATH_KEY);
    if (returnPath) {
      sessionStorage.removeItem(RETURN_PATH_KEY);
      // Use replace so the '/'-landing after redirect isn't in history
      navigate(returnPath, { replace: true });
    }
  }, [loggedIn, navigate]);

  // ── Loading / initialising ──────────────────────────────────────────────
  if (!isInitialized || isAuthLoading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={styles.label}>Signing you in…</p>
      </div>
    );
  }

  // ── SSO silent failed — ask for UPN ────────────────────────────────────
  if (showAuthInput) {
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h2 style={styles.heading}>Sign in</h2>
          <p style={styles.sub}>Enter your KPMG email address to continue.</p>
          <input
            id="authInput"
            type="email"
            placeholder="you@kpmg.com"
            style={styles.input}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                authenticateWithUserId((e.target as HTMLInputElement).value.trim());
              }
            }}
          />
          <button
            style={styles.button}
            onClick={() => {
              const el = document.getElementById('authInput') as HTMLInputElement | null;
              if (el) authenticateWithUserId(el.value.trim());
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ── Authenticated ───────────────────────────────────────────────────────
  if (loggedIn) {
    return <>{children}</>;
  }

  // ── Redirect in progress (blank while MSAL navigates) ──────────────────
  return (
    <div style={styles.center}>
      <div style={styles.spinner} />
      <p style={styles.label}>Redirecting to login…</p>
    </div>
  );
}

// ── Inline styles (no external CSS dep) ───────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    fontFamily: 'Open Sans, sans-serif',
    background: '#f4f6fb',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '4px solid #d0d7e3',
    borderTopColor: '#00338d',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  label: {
    marginTop: 16,
    color: '#4a5568',
    fontSize: 14,
  },
  card: {
    background: '#fff',
    borderRadius: 8,
    padding: '40px 48px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 340,
  },
  heading: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: '#00338d',
  },
  sub: {
    margin: 0,
    fontSize: 14,
    color: '#6b7280',
  },
  input: {
    padding: '10px 14px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    outline: 'none',
  },
  button: {
    padding: '10px 0',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    background: '#00338d',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
};
